/** Предварительный детальный анализ без ИИ — знаки и числа из даты рождения. */

import { getNumerologyPairCopy } from "./numerologyPairAnalysis";
import { getZodiacPairCopy } from "./zodiacPairAnalysis";
import { nameGenitiveOrPronoun } from "../data/profileCardCopy";

type DetailedAnalysisInput = {
  selfName: string;
  selfBirthDate: string;
  peerName: string;
  peerBirthDate: string;
  peerAge: number;
  compatibility: number;
  /** Пол партнёра — для согласования «указал/указала» и склонения имени. */
  peerGender?: "male" | "female";
};

type ZodiacInfo = {
  sign: string;
  signGenitive: string;
  element: "fire" | "earth" | "air" | "water";
  elementRu: string;
};

const ZODIAC_BY_MD: { sign: string; signGenitive: string; element: ZodiacInfo["element"]; elementRu: string; from: [number, number]; to: [number, number] }[] = [
  { sign: "Козерог", signGenitive: "Козерога", element: "earth", elementRu: "земля", from: [12, 22], to: [1, 19] },
  { sign: "Водолей", signGenitive: "Водолея", element: "air", elementRu: "воздух", from: [1, 20], to: [2, 18] },
  { sign: "Рыбы", signGenitive: "Рыб", element: "water", elementRu: "вода", from: [2, 19], to: [3, 20] },
  { sign: "Овен", signGenitive: "Овна", element: "fire", elementRu: "огонь", from: [3, 21], to: [4, 19] },
  { sign: "Телец", signGenitive: "Тельца", element: "earth", elementRu: "земля", from: [4, 20], to: [5, 20] },
  { sign: "Близнецы", signGenitive: "Близнецов", element: "air", elementRu: "воздух", from: [5, 21], to: [6, 20] },
  { sign: "Рак", signGenitive: "Рака", element: "water", elementRu: "вода", from: [6, 21], to: [7, 22] },
  { sign: "Лев", signGenitive: "Льва", element: "fire", elementRu: "огонь", from: [7, 23], to: [8, 22] },
  { sign: "Дева", signGenitive: "Девы", element: "earth", elementRu: "земля", from: [8, 23], to: [9, 22] },
  { sign: "Весы", signGenitive: "Весов", element: "air", elementRu: "воздух", from: [9, 23], to: [10, 22] },
  { sign: "Скорпион", signGenitive: "Скорпиона", element: "water", elementRu: "вода", from: [10, 23], to: [11, 21] },
  { sign: "Стрелец", signGenitive: "Стрельца", element: "fire", elementRu: "огонь", from: [11, 22], to: [12, 21] },
];

const ZODIAC_COMPAT: Record<string, string[]> = {
  Овен: ["Лев", "Стрелец", "Близнецы", "Водолей"],
  Телец: ["Дева", "Козерог", "Рак", "Рыбы"],
  Близнецы: ["Весы", "Водолей", "Овен", "Лев"],
  Рак: ["Скорпион", "Рыбы", "Телец", "Дева"],
  Лев: ["Овен", "Стрелец", "Близнецы", "Весы"],
  Дева: ["Телец", "Козерог", "Рак", "Скорпион"],
  Весы: ["Близнецы", "Водолей", "Лев", "Стрелец"],
  Скорпион: ["Рак", "Рыбы", "Дева", "Козерог"],
  Стрелец: ["Овен", "Лев", "Весы", "Водолей"],
  Козерог: ["Телец", "Дева", "Скорпион", "Рыбы"],
  Водолей: ["Близнецы", "Весы", "Овен", "Стрелец"],
  Рыбы: ["Рак", "Скорпион", "Телец", "Козерог"],
};

const ELEMENT_COMPAT: Record<ZodiacInfo["element"], ZodiacInfo["element"][]> = {
  fire: ["fire", "air"],
  earth: ["earth", "water"],
  air: ["air", "fire"],
  water: ["water", "earth"],
};

const LIFE_PATH_COMPAT: Record<number, number[]> = {
  1: [1, 5, 7],
  2: [2, 4, 8],
  3: [3, 6, 9],
  4: [2, 4, 8],
  5: [1, 5, 7],
  6: [3, 6, 9],
  7: [1, 5, 7],
  8: [2, 4, 8],
  9: [3, 6, 9],
};

function mdValue(month: number, day: number): number {
  return month * 100 + day;
}

function isInZodiacRange(month: number, day: number, from: [number, number], to: [number, number]): boolean {
  const v = mdValue(month, day);
  const fromV = mdValue(from[0], from[1]);
  const toV = mdValue(to[0], to[1]);
  if (fromV <= toV) return v >= fromV && v <= toV;
  return v >= fromV || v <= toV;
}

/**
 * Дата рождения — только настоящая.
 *
 * Раньше рядом жил `approximateBirthDateFromAge`: при пустой дате он возвращал
 * `<год>-07-01`, то есть всегда «Рак» и всегда одно и то же число жизненного пути,
 * а тексты ниже печатали эту выдумку как факт («дата рождения — 1 июля 1998»).
 * Тот же дефект был и на бэкенде (pipeline._chart_for подставлял 01.07.1990).
 * Нет даты — нет разбора: вызывающий обязан попросить заполнить профиль.
 */
function normalizeBirthDate(iso: string | undefined): string {
  const raw = (iso ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
}

/** Текст-заглушка вместо разбора, посчитанного от несуществующей даты. */
function needBirthDateText(selfMissing: boolean, peerMissing: boolean, peerName: string, peerGender?: "male" | "female"): string {
  if (selfMissing && peerMissing) {
    return `Разбор строится по датам рождения — у вас и у ${nameGenitiveOrPronoun(peerName, peerGender)} они пока не заполнены.`;
  }
  if (selfMissing) {
    return "Разбор строится по дате рождения — укажите её в своём профиле, и мы всё посчитаем.";
  }
  const указал = peerGender === "female" ? "указала" : "указал";
  return `Разбор строится по датам рождения. ${peerName} свою пока не ${указал} — как только появится, разбор станет доступен.`;
}

function parseBirthDate(iso: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month || d.getUTCDate() !== day) {
    return null;
  }
  return { year, month, day };
}

function zodiacFromBirthDate(iso: string): ZodiacInfo | null {
  const parsed = parseBirthDate(iso);
  if (!parsed) return null;
  const row = ZODIAC_BY_MD.find((z) => isInZodiacRange(parsed.month, parsed.day, z.from, z.to));
  if (!row) return null;
  return { sign: row.sign, signGenitive: row.signGenitive, element: row.element, elementRu: row.elementRu };
}

function reduceNumerology(n: number): number {
  let x = Math.abs(n);
  while (x > 9 && x !== 11 && x !== 22 && x !== 33) {
    x = String(x)
      .split("")
      .reduce((sum, digit) => sum + Number(digit), 0);
  }
  return x || 9;
}

/** Число жизненного пути по дате рождения (классическая нумерология). */
function lifePathFromBirthDate(iso: string): number | null {
  const parsed = parseBirthDate(iso);
  if (!parsed) return null;
  const sum = String(parsed.year) + String(parsed.month).padStart(2, "0") + String(parsed.day).padStart(2, "0");
  return reduceNumerology(sum.split("").reduce((acc, ch) => acc + Number(ch), 0));
}

function pairLifePathNumber(a: number, b: number): number {
  return reduceNumerology(a + b);
}

function formatBirthDateRu(iso: string): string {
  const parsed = parseBirthDate(iso);
  if (!parsed) return iso;
  const months = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
  return `${parsed.day} ${months[parsed.month - 1]} ${parsed.year}`;
}

function generateAstrologyAnalysisStub(input: DetailedAnalysisInput): string {
  const selfDate = normalizeBirthDate(input.selfBirthDate);
  const peerDate = normalizeBirthDate(input.peerBirthDate);
  if (!selfDate || !peerDate) {
    return needBirthDateText(!selfDate, !peerDate, input.peerName, input.peerGender);
  }

  const selfZodiac = zodiacFromBirthDate(selfDate);
  const peerZodiac = zodiacFromBirthDate(peerDate);
  if (!selfZodiac || !peerZodiac) {
    return "Не удалось определить знаки зодиака по датам рождения. Укажите дату рождения в профиле и попробуйте снова.";
  }

  const zodiacMatch = (ZODIAC_COMPAT[selfZodiac.sign] ?? []).includes(peerZodiac.sign);
  const elementMatch = (ELEMENT_COMPAT[selfZodiac.element] ?? []).includes(peerZodiac.element);
  const pairCopy = getZodiacPairCopy(selfZodiac.sign, peerZodiac.sign);
  const { compatibility } = input;

  const verdict =
    compatibility >= 80
      ? "Общий процент совместимости высокий — пара выглядит перспективной."
      : compatibility >= 60
        ? "Средний и выше процент — есть потенциал при внимании к различиям."
        : "Процент ниже среднего — паре важнее терпение и честный диалог.";

  const header = `🌟 Астрологическая совместимость: ${selfZodiac.signGenitive} (${input.selfName}) и ${peerZodiac.signGenitive} (${input.peerName})

Ваш знак: ${selfZodiac.sign} (${selfZodiac.elementRu}), дата рождения — ${formatBirthDateRu(selfDate)}.
Знак партнёра: ${peerZodiac.sign} (${peerZodiac.elementRu}), дата рождения — ${formatBirthDateRu(peerDate)}.
Классическая матрица: ${zodiacMatch ? "знаки считаются совместимыми" : selfZodiac.sign === peerZodiac.sign ? "один и тот же знак" : "сочетание требует работы"}. Стихии ${elementMatch ? "поддерживают друг друга" : "различаются"}.
${verdict}`;

  if (!pairCopy) {
    return `${header}\n\nНе удалось подобрать описание для этой пары знаков.`;
  }

  return `${header}

✅ Что складывается хорошо:
${pairCopy.positive}

⚠️ На что обратить внимание:
${pairCopy.negative}

💡 Практический совет:
${pairCopy.tip}`;
}

function generateNumerologyAnalysisStub(input: DetailedAnalysisInput): string {
  const selfDate = normalizeBirthDate(input.selfBirthDate);
  const peerDate = normalizeBirthDate(input.peerBirthDate);
  if (!selfDate || !peerDate) {
    return needBirthDateText(!selfDate, !peerDate, input.peerName, input.peerGender);
  }

  const yourNumber = lifePathFromBirthDate(selfDate);
  const theirNumber = lifePathFromBirthDate(peerDate);
  if (yourNumber == null || theirNumber == null) {
    return "Не удалось рассчитать числа жизненного пути. Укажите дату рождения в профиле и попробуйте снова.";
  }

  const destinyNumber = pairLifePathNumber(yourNumber, theirNumber);
  const pathsMatch = (LIFE_PATH_COMPAT[yourNumber] ?? []).includes(theirNumber);
  const pairCopy = getNumerologyPairCopy(yourNumber, theirNumber);
  const { compatibility } = input;

  const header = `🔢 Нумерологический анализ: число ${yourNumber} (${input.selfName}) и число ${theirNumber} (${input.peerName})

Числа жизненного пути по датам рождения: ${formatBirthDateRu(selfDate)} → ${yourNumber}, ${formatBirthDateRu(peerDate)} → ${theirNumber}.
Число пары (сумма путей): ${destinyNumber}. Матрица нумерологии: ${pathsMatch ? "пути хорошо сочетаются" : yourNumber === theirNumber ? "одинаковые числа" : "разные вибрации"}.
Общая совместимость в приложении: ${compatibility}%.`;

  return `${header}

✅ Что складывается хорошо:
${pairCopy.positive}

⚠️ На что обратить внимание:
${pairCopy.negative}

💡 Практический совет:
${pairCopy.tip}`;
}

export function buildDetailedAnalysisTexts(input: DetailedAnalysisInput): { astrology: string; numerology: string } {
  return {
    astrology: generateAstrologyAnalysisStub(input),
    numerology: generateNumerologyAnalysisStub(input),
  };
}

type ResolvedAstrology = {
  zodiacSign: string;
  element: ZodiacInfo["element"];
  moonSign: string;
  ascendant: string;
};

/** Знак и стихия: из API или по дате рождения / возрасту. */
export function resolveAstrologyFromProfile(
  birthDate: string | undefined,
  age: number,
  api?: { zodiacSign?: unknown; sign?: unknown; element?: unknown; moonSign?: unknown; ascendant?: unknown } | null,
): ResolvedAstrology {
  const empty: ResolvedAstrology = { zodiacSign: "", element: "air", moonSign: "", ascendant: "" };
  const fromApi = String(api?.zodiacSign ?? api?.sign ?? "").trim();
  if (fromApi) {
    const row = ZODIAC_BY_MD.find((z) => z.sign === fromApi);
    const elRaw = String(api?.element ?? "").toLowerCase();
    const element =
      elRaw === "fire" || elRaw === "earth" || elRaw === "air" || elRaw === "water"
        ? (elRaw as ZodiacInfo["element"])
        : row?.element ?? "air";
    return {
      zodiacSign: fromApi,
      element,
      moonSign: String(api?.moonSign ?? ""),
      ascendant: String(api?.ascendant ?? ""),
    };
  }
  void age; // возраст больше не годится в замену даты — см. normalizeBirthDate
  const bd = normalizeBirthDate(birthDate);
  if (!bd) return empty;
  const z = zodiacFromBirthDate(bd);
  if (!z) return empty;
  return { zodiacSign: z.sign, element: z.element, moonSign: "", ascendant: "" };
}

/** Число жизненного пути из даты рождения, если в API не задано. */
export function resolveNumerologyFromProfile(
  birthDate: string | undefined,
  age: number,
  api?: { lifePath?: unknown; lifePathNumber?: unknown; soulUrge?: unknown; destiny?: unknown } | null,
): { lifePath: number; soulUrge: number; destiny: number } {
  const soulUrge = Number(api?.soulUrge ?? 1) || 1;
  const destiny = Number(api?.destiny ?? 1) || 1;
  const fromApi = Number(api?.lifePath ?? api?.lifePathNumber ?? 0);
  if (fromApi >= 1 && fromApi <= 33) {
    return { lifePath: fromApi, soulUrge, destiny };
  }
  void age; // возраст больше не годится в замену даты — см. normalizeBirthDate
  const bd = normalizeBirthDate(birthDate);
  const lp = bd ? lifePathFromBirthDate(bd) : null;
  return { lifePath: lp ?? 1, soulUrge, destiny };
}

export function profileHasZodiacData(birthDate: string | undefined, age: number, zodiacSign: string): boolean {
  // ВАЖНО: только реальные данные. Убранный фолбэк «возраст → дата» давал
  // `<год>-07-01`, то есть всегда «Рак», и профиль без даты рождения молча получал
  // выдуманный знак, который затем шёл в расчёт совместимости с полным весом 30.
  void age;
  const raw = (birthDate ?? "").trim();
  return Boolean(zodiacSign.trim() || /^\d{4}-\d{2}-\d{2}$/.test(raw));
}

export function profileHasBirthNumerology(birthDate: string | undefined, age: number): boolean {
  // Как и в profileHasZodiacData: считаем только по реальной дате. Иначе жизненный путь
  // вычислялся от подставного 1 июля и подавался в отчёте как факт.
  void age;
  const raw = (birthDate ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  return Boolean(lifePathFromBirthDate(raw));
}
